import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Tag, Space, Card, Statistic, Spin, Progress } from 'antd';
import { PlayCircleOutlined, PauseOutlined, StopOutlined, MessageOutlined, SyncOutlined, FilterOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import AdminMenu from '../components/AdminMenu';
import api from '../services/api';


dayjs.extend(duration);
dayjs.extend(relativeTime);

const API_BASE_URL = import.meta.env.MODE === 'development'
  ? 'https://mundiemby.us.to'
  : 'https://mundiemby.us.to';

const { Option } = Select;
const { Search } = Input;

const AdminSesiones = () => {
  const [sesiones, setSesiones] = useState([]);
  const [embyServers, setEmbyServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverLoading, setServerLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    metodo: '',
    usuario: '',
    servidor: '',
    accion: ''
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [header, setHeader] = useState('Mensaje del administrador');
  const [sesionSeleccionada, setSesionSeleccionada] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    reproduciendo: 0,
    pausados: 0
  });

  // Estado para el tiempo de reproducción en ticks por sesión
  const [playTimes, setPlayTimes] = useState({});
  const playTimesRef = useRef(playTimes);
  playTimesRef.current = playTimes;

  const handleApiError = (error, context) => {
    console.error(`Error en ${context}:`, error);
    let errorMessage = 'Error desconocido';
    if (error.response) {
      errorMessage = `Error ${error.response.status}: ${error.response.data?.message || 'Sin detalles'}`;
    } else if (error.request) {
      errorMessage = 'No se recibió respuesta del servidor';
    } else {
      errorMessage = error.message;
    }
    message.error(`${context}: ${errorMessage}`);
    return errorMessage;
  };

  const obtenerServidores = useCallback(async () => {
    try {
      const response = await api.get('/sesiones/servidores', {
        timeout: 8000
      });
      
      if (response.data?.success) {
        setEmbyServers(response.data.data);
      } else {
        message.error(response.data?.message || 'Respuesta inesperada del servidor');
      }
    } catch (error) {
      handleApiError(error, 'Error al obtener servidores');
      setEmbyServers([
        { nombre: 'Mundiemby1', url: 'http://mundiemby.us.to:33421' },
        { nombre: 'Mundiemby2', url: 'http://mundiemby.us.to:33428' },
        { nombre: 'Mundiemby3', url: 'http://mundiemby.us.to:33423' },
        { nombre: 'Mundiemby4', url: 'http://mundiemby.us.to:33426' },
        { nombre: 'Mundiemby5', url: 'http://mundiemby.us.to:33422' },
        { nombre: 'Mundiemby6', url: 'http://mundiemby.us.to:33424' },
        { nombre: 'Mundiemby7', url: 'http://mundiemby.us.to:33427' }
      ]);
    } finally {
      setServerLoading(false);
    }
  }, []);

  const obtenerSesiones = useCallback(async () => {
    if (serverLoading) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.metodo) params.append('metodo', filtros.metodo);
      if (filtros.usuario) params.append('usuario', filtros.usuario);
      if (filtros.servidor) params.append('servidor', filtros.servidor);
      if (filtros.accion) params.append('accion', filtros.accion);

      const response = await api.get(`/sesiones?${params.toString()}`, {
        timeout: 20000
      });
      

      if (response.data?.success) {
        const sesionesData = response.data.data.map(sesion => ({
          ...sesion,
          server: sesion.server || sesion.ServerId || 'Desconocido',
          UserName: sesion.UserName || sesion.userName || 'Anon',
          DeviceName: sesion.DeviceName || 'Desconocido',
          PlayState: sesion.PlayState || {},
          NowPlayingItem: sesion.NowPlayingItem || null,
          Id: sesion.Id || sesion.id || '',
        }));

        setSesiones(sesionesData);

        // Inicializa playTimes con PositionTicks para sesiones reproduciéndose
        const nuevosPlayTimes = {};
        sesionesData.forEach(sesion => {
          if (sesion.PlayState && !sesion.PlayState.IsPaused) {
            nuevosPlayTimes[sesion.Id] = sesion.PlayState.PositionTicks || 0;
          }
        });
        setPlayTimes(nuevosPlayTimes);

        const sesionesValidas = sesionesData.filter(s =>
          s.NowPlayingItem &&
          s.NowPlayingItem.Name &&
          s.NowPlayingItem.Name !== 'Sin título'
        );
        
        
        
        const total = sesionesValidas.length;
        const reproduciendo = sesionesValidas.filter(s => s.PlayState && !s.PlayState.IsPaused).length;
        const pausados = sesionesValidas.filter(s => s.PlayState && s.PlayState.IsPaused).length;
        setStats({ total, reproduciendo, pausados });
      } else {
        message.error(response.data?.message || 'Error al procesar sesiones');
      }
    } catch (error) {
      handleApiError(error, 'Error al obtener sesiones');
      setSesiones([]);
      setStats({ total: 0, reproduciendo: 0, pausados: 0 });
    } finally {
      setLoading(false);
    }
  }, [filtros, serverLoading]);

  const controlarReproduccion = async (sessionId, server, accion) => {
    try {
      const response = await api.post('/sesiones/control',
        { server, sessionId, accion },
        { timeout: 8000 }
      );
      
      if (response.data?.success) {
        message.success(`Reproducción ${accion} correctamente`);
        obtenerSesiones();
      } else {
        message.error(response.data?.message || `Error al ${accion} reproducción`);
      }
    } catch (error) {
      handleApiError(error, `Error al ${accion} reproducción`);
    }
  };

  const enviarMensaje = async () => {
    if (!sesionSeleccionada) return;
    try {
      const response = await api.post('/sesiones/mensaje',
        {
          server: sesionSeleccionada.server,
          sessionId: sesionSeleccionada.Id,
          mensaje,
          header
        },
        { timeout: 8000 }
      );
      
      if (response.data?.success) {
        message.success('Mensaje enviado correctamente');
        setModalVisible(false);
        setMensaje('');
      } else {
        message.error(response.data?.message || 'Error al enviar mensaje');
      }
    } catch (error) {
      handleApiError(error, 'Error al enviar mensaje');
    }
  };

  // Actualiza playTimes cada segundo para sesiones no pausadas
  useEffect(() => {
    const interval = setInterval(() => {
      setPlayTimes(current => {
        const updated = { ...current };
        let changed = false;
        Object.entries(updated).forEach(([sessionId, ticks]) => {
          // Incrementar 1 segundo (ticks son en 100-nanosegundos, 1s = 10,000,000 ticks)
          updated[sessionId] = ticks + 10000000;
          changed = true;
        });
        return changed ? updated : current;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'UserName',
      key: 'UserName',
      render: (text, record) => (
        <div style={{ fontSize: '0.9rem' }}>
          <strong>{text}</strong>
          <div>
            <Tag color="blue" style={{ fontSize: '0.75rem' }}>{record.Client}</Tag>
            {record.DeviceName && <Tag style={{ fontSize: '0.75rem' }}>{record.DeviceName}</Tag>}
          </div>
        </div>
      ),
      sorter: (a, b) => a.UserName.localeCompare(b.UserName),
      width: 180
    },
    {
      title: 'Contenido',
      dataIndex: 'NowPlayingItem',
      key: 'NowPlayingItem',
      render: (item, record) => item ? (
        <div style={{ fontSize: '0.85rem' }}>
          <div><strong>{item.Name}</strong></div>
          <div style={{ color: '#888' }}>{item.SeriesName || item.Album || ''}</div>
          <div style={{ marginTop: 4 }}>
            <Tag color="purple" style={{ fontSize: '0.75rem' }}>{item.Type}</Tag>
            {record.PlayState && (
              <Tag color={record.PlayState.IsPaused ? 'orange' : 'green'} style={{ fontSize: '0.75rem' }}>
                {record.PlayState.IsPaused ? 'Pausado' : 'Reproduciendo'}
              </Tag>
            )}
          </div>
        </div>
      ) : <Tag style={{ fontSize: '0.85rem' }}>No disponible</Tag>,
      width: 240
    },
    {
      title: 'Servidor',
      dataIndex: 'server',
      key: 'server',
      render: (text) => <Tag color="geekblue" style={{ fontSize: '0.8rem' }}>{text}</Tag>,
      sorter: (a, b) => a.server.localeCompare(b.server),
      width: 100
    },
    {
      title: 'Método',
      dataIndex: 'PlayState',
      key: 'PlayMethod',
      render: (playState) => playState ? (
        <Tag color="cyan" style={{ fontSize: '0.8rem' }}>{playState.PlayMethod}</Tag>
      ) : <Tag style={{ fontSize: '0.8rem' }}>Desconocido</Tag>,
      sorter: (a, b) => (a.PlayState?.PlayMethod || '').localeCompare(b.PlayState?.PlayMethod || ''),
      width: 100
    },
    {
      title: 'Tiempo',
      dataIndex: 'PlayState',
      key: 'time',
      render: (playState, record) => {
        if (!playState) return <Tag style={{ fontSize: '0.8rem' }}>No disponible</Tag>;

        // Usa playTimes actualizado si está en estado reproduciendo
        const ticks = (playState.IsPaused ? playState.PositionTicks : (playTimes[record.Id] ?? playState.PositionTicks)) || 0;
        const ms = ticks / 10000;
        const formattedTime = dayjs.duration(ms).format('HH:mm:ss');

        return (
          <div style={{ fontSize: '0.85rem' }}>
            <div>{formattedTime}</div>
            <Tag style={{ fontSize: '0.75rem' }}>{dayjs(record.LastActivityDate).fromNow()}</Tag>
          </div>
        );
      },
      width: 110
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {record.PlayState?.IsPaused ? (
            <Button
              icon={<PlayCircleOutlined />}
              onClick={() => controlarReproduccion(record.Id, record.server, 'play')}
              type="primary"
              size="small"
              ghost
            />
          ) : (
            <Button
              icon={<PauseOutlined />}
              onClick={() => controlarReproduccion(record.Id, record.server, 'pause')}
              type="primary"
              size="small"
              ghost
            />
          )}
          <Button
            icon={<StopOutlined />}
            onClick={() => controlarReproduccion(record.Id, record.server, 'stop')}
            danger
            size="small"
            ghost
          />
          <Button
            icon={<MessageOutlined />}
            onClick={() => {
              setSesionSeleccionada(record);
              setModalVisible(true);
            }}
            type="dashed"
            size="small"
          />
        </Space>
      ),
      width: 160
    }
  ];

  useEffect(() => {
    obtenerServidores();
  }, [obtenerServidores]);

  useEffect(() => {
    if (!serverLoading) {
      obtenerSesiones();
      // const interval = setInterval(obtenerSesiones, 15000);
      // return () => clearInterval(interval);
    }
  }, [obtenerSesiones, serverLoading]);

  if (serverLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Cargando configuración inicial...</div>
      </div>
    );
  }
  

  return (
    <>
    <AdminMenu />
    <div style={{ padding: 16, fontSize: '0.85rem', marginTop: 64 }}>
      <div style={{ marginBottom: 16 }}>
        <Card size="small">
          <Space size="middle">
            <Statistic title="Total sesiones" value={stats.total} />
            <Statistic title="Reproduciendo" value={stats.reproduciendo} valueStyle={{ color: '#52c41a' }} />
            <Statistic title="Pausados" value={stats.pausados} valueStyle={{ color: '#faad14' }} />
          </Space>
        </Card>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Card
          size="small"
          title={
            <Space>
              <FilterOutlined />
              <span>Filtros</span>
            </Space>
          }
        >
          <Form layout="inline" size="small">
            <Form.Item label="Método">
              <Select
                placeholder="Filtrar por método"
                style={{ width: 160 }}
                allowClear
                onChange={value => setFiltros({ ...filtros, metodo: value })}
                value={filtros.metodo || undefined}
              >
                <Option value="Transcode">Transcode</Option>
                <Option value="DirectStream">DirectStream</Option>
                <Option value="DirectPlay">DirectPlay</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Usuario">
              <Search
                placeholder="Buscar usuario"
                allowClear
                onSearch={value => setFiltros({ ...filtros, usuario: value })}
                onChange={e => setFiltros({ ...filtros, usuario: e.target.value })}
                value={filtros.usuario}
                style={{ width: 180 }}
              />
            </Form.Item>

            <Form.Item label="Servidor">
              <Select
                placeholder="Filtrar por servidor"
                style={{ width: 140 }}
                allowClear
                onChange={value => setFiltros({ ...filtros, servidor: value })}
                value={filtros.servidor || undefined}
                loading={serverLoading}
              >
                {embyServers.map(server => (
                  <Option key={server.nombre} value={server.nombre}>
                    {server.nombre}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Acción">
              <Select
                placeholder="Filtrar por acción"
                style={{ width: 140 }}
                allowClear
                onChange={value => setFiltros({ ...filtros, accion: value })}
                value={filtros.accion || undefined}
              >
                <Option value="reproduciendo">Reproduciendo</Option>
                <Option value="pausado">Pausado</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Button icon={<SyncOutlined />} onClick={obtenerSesiones} loading={loading} type="primary" size="small">
                Actualizar
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>

      <Card size="small" styles={{ body: { padding: 12 } }}>
  {loading ? (
    <Spin tip="Cargando sesiones..." />
  ) : sesiones.length === 0 ? (
    <Card>No hay sesiones activas</Card>
  ) : (
    <div
  style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 12,
  }}
>

{sesiones
  .filter(sesion =>
    sesion.NowPlayingItem &&
    sesion.NowPlayingItem.Name &&
    sesion.NowPlayingItem.Name !== 'Sin título'
  )
  .map((sesion) => {
        const item = sesion.NowPlayingItem || {};
        const playState = sesion.PlayState || {};
        const ticks = (playState.IsPaused ? playState.PositionTicks : (playTimes[sesion.Id] ?? playState.PositionTicks)) || 0;
        const durTicks = item.RunTimeTicks || 1;
        const progress = Math.min(100, Math.round((ticks / durTicks) * 100));
        const imagen = item.Id
          ? `${API_BASE_URL}/emby/Items/${item.Id}/Images/Primary?maxHeight=100`
          : 'https://via.placeholder.com/80x100.png?text=No+Image';

        return (
          <Card key={sesion.Id} size="small" style={{ display: 'flex', gap: 12 }}>
            <img src={imagen} alt="poster" style={{ height: 100, borderRadius: 4 }} />
            <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>
  {item.SeriesName && item.Name
    ? `${item.SeriesName} - ${item.Name}`
    : item.Name || item.Album || item.EpisodeTitle || item.Type || 'Sin título'}
</div>

<div style={{ fontSize: '0.85rem', color: '#888' }}>
  {item.ProductionYear ? `(${item.ProductionYear})` : ''}
</div>
<Progress percent={progress} showInfo={false} size="small" strokeColor="#52c41a" style={{ marginTop: 4 }} />
              <div style={{ fontSize: '0.8rem', marginTop: 4 }}>
  {dayjs.duration(ticks / 10000).format('H:mm:ss')} / {dayjs.duration(durTicks / 10000).format('H:mm:ss')}
</div>
              <div style={{ fontSize: '0.8rem', marginTop: 8 }}>
                <div><b>Dispositivo:</b> {sesion.DeviceName}</div>
                <div><b>Cliente:</b> {sesion.Client || ''} {sesion.ApplicationVersion || ''}</div>
                <div><b>IP:</b> {sesion.RemoteEndPoint || ''}</div>
                <div><b>Video:</b> {item.VideoCodec || ''} - {playState.PlayMethod || ''}</div>
                <div><b>Audio:</b> {item.AudioCodec || ''}</div>
              </div>
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                <Tag color="geekblue" style={{ fontSize: '0.85rem' }}>{sesion.UserName}</Tag>
                <Space>
                  {playState.IsPaused ? (
                    <Button
                      icon={<PlayCircleOutlined />}
                      onClick={() => controlarReproduccion(sesion.Id, sesion.server, 'play')}
                      size="small"
                    />
                  ) : (
                    <Button
                      icon={<PauseOutlined />}
                      onClick={() => controlarReproduccion(sesion.Id, sesion.server, 'pause')}
                      size="small"
                    />
                  )}
                  <Button
                    icon={<StopOutlined />}
                    danger
                    onClick={() => controlarReproduccion(sesion.Id, sesion.server, 'stop')}
                    size="small"
                  />
                  <Button
                    icon={<MessageOutlined />}
                    onClick={() => {
                      setSesionSeleccionada(sesion);
                      setModalVisible(true);
                    }}
                    size="small"
                  />
                </Space>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  )}
</Card>



      <Modal
        title={`Enviar mensaje a ${sesionSeleccionada?.UserName || 'usuario'}`}
        open={modalVisible}
        onOk={enviarMensaje}
        onCancel={() => setModalVisible(false)}
        okText="Enviar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !mensaje.trim() }}
        width={400}
      >
        <Form layout="vertical" size="small">
          <Form.Item label="Título del mensaje">
            <Input
              value={header}
              onChange={e => setHeader(e.target.value)}
              placeholder="Ingrese el título del mensaje"
            />
          </Form.Item>
          <Form.Item label="Mensaje">
            <Input.TextArea
              rows={4}
              value={mensaje}
              onChange={e => setMensaje(e.target.value)}
              placeholder="Escriba el mensaje que desea enviar"
              showCount
              maxLength={500}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
    </>
  );
};

export default AdminSesiones;
